import React, { useEffect, useMemo, useState } from 'react';
import {
    AdminChecklistTemplate,
    AdminCreateChecklistTemplateDto,
    AdminUpdateChecklistTemplateDto,
    AdminChecklistPhaseDto,
    LanguageAdminDto,
} from '../../types';
import { getAllAdminChecklistPhases, getAllLanguagesAdmin } from '../../services/api.admin';
import AdminModalShell from './AdminModalShell';

interface ChecklistModalProps {
    isOpen: boolean;
    onClose: () => void;
    isCreating: boolean;
    selectedTemplate: AdminChecklistTemplate | null;
    createFormData: AdminCreateChecklistTemplateDto;
    setCreateFormData: React.Dispatch<React.SetStateAction<AdminCreateChecklistTemplateDto>>;
    updateFormData: AdminUpdateChecklistTemplateDto;
    setUpdateFormData: React.Dispatch<React.SetStateAction<AdminUpdateChecklistTemplateDto>>;
    handleSaveChecklist: () => void;
}

const emptyLangMap = (langs: string[]): Record<string, string> =>
    Object.fromEntries(langs.map((l) => [l, '']));

const ChecklistModal: React.FC<ChecklistModalProps> = ({
    isOpen,
    onClose,
    isCreating,
    selectedTemplate: _selectedTemplate,
    createFormData,
    setCreateFormData,
    updateFormData,
    setUpdateFormData,
    handleSaveChecklist,
}) => {
    const [availablePhases, setAvailablePhases] = useState<AdminChecklistPhaseDto[]>([]);
    const [languages, setLanguages] = useState<LanguageAdminDto[]>([]);
    const [editLang, setEditLang] = useState('nl-be');

    const langCodes = useMemo(
        () => (languages.length > 0 ? languages.map((l) => l.code) : ['en', 'nl-be', 'fr-be']),
        [languages]
    );

    useEffect(() => {
        if (!isOpen) return;
        Promise.all([
            getAllAdminChecklistPhases(),
            getAllLanguagesAdmin().catch(() => [] as LanguageAdminDto[]),
        ]).then(([phases, langs]) => {
            setAvailablePhases(phases);
            const list = Array.isArray(langs) ? langs : [];
            setLanguages(list);
            if (list.length) {
                const preferred =
                    list.find((l) => l.code === 'nl-be')?.code ||
                    list.find((l) => l.code === 'en')?.code ||
                    list[0].code;
                setEditLang((prev) => (list.some((l) => l.code === prev) ? prev : preferred));
            }
        });
    }, [isOpen]);

    // Ensure form names map has all language keys when langs load / modal opens
    useEffect(() => {
        if (!isOpen || langCodes.length === 0) return;
        if (isCreating) {
            setCreateFormData((prev) => ({
                ...prev,
                names: { ...emptyLangMap(langCodes), ...(prev.names || {}) },
            }));
        } else {
            setUpdateFormData((prev) => ({
                ...prev,
                names: { ...emptyLangMap(langCodes), ...(prev.names || {}) },
            }));
        }
    }, [isOpen, isCreating, langCodes, setCreateFormData, setUpdateFormData]);

    if (!isOpen) return null;

    const setFormData = isCreating ? setCreateFormData : setUpdateFormData;
    const formData = isCreating ? createFormData : updateFormData;
    const names = formData.names || {};

    const handleNameChange = (value: string) => {
        setFormData((prev) => ({
            ...prev,
            names: { ...(prev.names || {}), [editLang]: value },
        }));
    };

    const togglePhase = (phaseId: number) => {
        setFormData((prev) => {
            const current = prev.phaseIds || [];
            const newPhases = current.includes(phaseId)
                ? current.filter((id) => id !== phaseId)
                : [...current, phaseId];
            return { ...prev, phaseIds: newPhases };
        });
    };

    const hasAnyName = Object.values(names).some((v) => (v || '').trim().length > 0);

    return (
        <AdminModalShell
            title={isCreating ? 'Create Checklist Template' : 'Edit Checklist Template'}
            onClose={onClose}
            size="lg"
        >
            <div className="grid grid-cols-1 gap-4">
                <div>
                    <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                        <label className="block text-sm font-medium text-gray-700">
                            Name (per language, required)
                        </label>
                        <div className="flex flex-wrap gap-1">
                            {langCodes.map((code) => {
                                const filled = !!(names[code] || '').trim();
                                const active = editLang === code;
                                return (
                                    <button
                                        key={code}
                                        type="button"
                                        onClick={() => setEditLang(code)}
                                        className={`px-2.5 py-1 text-xs rounded-md border transition-colors ${
                                            active
                                                ? 'bg-blue-600 text-white border-blue-600'
                                                : filled
                                                  ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
                                                  : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                                        }`}
                                    >
                                        {code}
                                        {filled ? ' ✓' : ''}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                    <input
                        type="text"
                        value={names[editLang] || ''}
                        onChange={(e) => handleNameChange(e.target.value)}
                        placeholder={`Name in ${editLang}`}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 shadow-sm"
                        required
                    />
                    <p className="mt-1 text-xs text-gray-500">
                        Shop users see the name for their language. Fill each language you support.
                    </p>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Assign to age phases (select one or more)
                    </label>
                    <p className="mb-2 text-xs text-gray-500">
                        Include <strong>Expecting</strong> for pregnancy items. One template can cover Expecting and
                        postnatal phases — no need for separate expecting/born rows.
                    </p>
                    <div className="grid grid-cols-2 gap-2 max-h-60 overflow-y-auto border border-gray-200 p-3 rounded-md">
                        {availablePhases.map((phase) => (
                            <label key={phase.id} className="flex items-center gap-2 text-sm cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={(formData.phaseIds || []).includes(phase.id)}
                                    onChange={() => togglePhase(phase.id)}
                                />
                                {phase.icon ? `${phase.icon} ` : ''}
                                {phase.name}{' '}
                                <span className="text-gray-400">
                                    {phase.minAgeMonths < 0
                                        ? '(Expecting)'
                                        : `(${phase.minAgeMonths}-${phase.maxAgeMonths} mo)`}
                                </span>
                            </label>
                        ))}
                    </div>
                </div>
            </div>

            <div className="mt-8 flex justify-end gap-3">
                <button
                    onClick={onClose}
                    className="px-5 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors font-medium"
                    type="button"
                >
                    Cancel
                </button>
                <button
                    onClick={handleSaveChecklist}
                    disabled={!hasAnyName}
                    className="px-5 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    type="button"
                >
                    Save Template
                </button>
            </div>
        </AdminModalShell>
    );
};

export default ChecklistModal;
